import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import axios, { CancelToken } from 'axios';
import ReactTimeout from 'react-timeout';
import classNames from 'classnames';
import Icon from 'components/Icon';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { differenceInMilliseconds, stringToDate, now } from 'helpers/DateTimeHelper';
import { noop } from 'helpers/GlobalHelper';
import {
  Events,
  handleAnalytics,
  SEARCH_INPUT_TRIGGER_INTERVAL_MS,
} from '@foxcorp/delta-web-analytics';
import SearchHeader from './SearchHeader';
import style from './Search.module.scss';

let axiosSource = null;
let inputChangeTimeout = null;

class Search extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userInput: '',
      response: null,
      loading: false,
      resultsTimedOut: false,
      updateExpiredTimeouts: [],
      timer: null,
    };

    this.debounceSearchRequest = _.debounce(this.searchRequest, 300);
    this.searchRequest = this.searchRequest.bind(this);
    this.clearTimer = this.clearTimer.bind(this);
    this.setTimedOut = this.setTimedOut.bind(this);
  }

  componentDidMount() {
    if (this.hasRequest()) {
      this.getRequest();
      this.setFocus();
    }
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  setFocus() {
    // https://facebook.github.io/react/docs/refs-and-the-dom.html
    const { autofocus } = this.props;
    if (autofocus !== false) {
      this.searchInput.focus();
    }
  }

  onToggleSearch() {
    const { toggleSearch } = this.props;
    toggleSearch(false);
  }

  searchRequest(url, headers, success, error) {
    const { setTimeout } = this.props;

    this.clearTimer();
    const timer = setTimeout(this.setTimedOut, 5000);
    this.setState({ timer });
    axiosSource = CancelToken.source();
    const Headers = headers;
    Headers.cancelToken = axiosSource.token;

    axios
      .get(url, Headers)
      .then(resp => resp)
      .then(success)
      .catch(error);
  }

  hasRequest() {
    const { request } = this.props;
    return request && request.headers && request.route;
  }

  getRequest() {
    this.setState({ loading: true });
    const { request, isValidInput, useDoubleQuotes } = this.props;
    const { userInput } = this.state;

    if (axiosSource) axiosSource.cancel();
    if (!isValidInput) {
      this.setState({ response: null, loading: false });
      return;
    }
    const trimmedInput = userInput.trim();
    const searchInput = useDoubleQuotes ? `%22${trimmedInput}%22` : trimmedInput;
    const url = `${request.route}${searchInput}${request.params || ''}`;
    this.setState({ resultsTimedOut: false });
    this.debounceSearchRequest(
      url,
      request.headers,
      response => {
        let res = response;
        // if there's no user input the response should be null, otherwise it will show response from previous query.
        if (!this.state.userInput) res = null;
        this.setState({ response: res, loading: false });
        this.setUpdateExpiredTimeouts({ response: res });
        axiosSource = null;
      },
      error => {
        axiosSource = null;
        const isAxiosCancel = axios.isCancel(error);
        // keep loading if the error is a canceled request
        if (isAxiosCancel) return;

        console.error(`Could not get data for ${request.route}`, error.message);
        this.setState({ loading: false });
      },
      searchInput,
    );
  }

  clearTimer() {
    const { timer } = this.state;
    const { clearTimeout } = this.props;
    if (!timer) return;
    clearTimeout(timer);
    this.setState({ timer: null });
  }

  setTimedOut() {
    this.setState({ resultsTimedOut: true });
  }

  handleSearchInputChange(e) {
    const {
      handleInputChange,
      setTimeout,
      clearTimeout,
      getSearchResultCount,
      handleSearchCompleted,
    } = this.props;
    const searchTerm = e.target.value.toLowerCase();
    handleInputChange({ input: searchTerm });
    if (inputChangeTimeout) clearTimeout(inputChangeTimeout);

    if (searchTerm) {
      inputChangeTimeout = setTimeout(() => {
        const searchResultCount = getSearchResultCount();

        handleAnalytics({
          event: Events.SEARCH_COMPLETED,
          payload: {
            page_search_term: searchTerm,
            page_num_search_results: searchResultCount,
          },
          tealiumPayload: {
            ...getCommonTealiumProps(),
          },
        });

        handleSearchCompleted();
      }, SEARCH_INPUT_TRIGGER_INTERVAL_MS);
    }

    this.setState(
      {
        userInput: searchTerm,
        response: null,
      },
      function _getRequest() {
        this.getRequest();
      },
    );
  }

  onClearSearchInputClick() {
    this.setState({ userInput: '' });
    this.setFocus();
  }

  setUpdateExpiredTimeouts({ response }) {
    const { updateExpiredTimeouts } = this.state;
    const { clearTimeout, setTimeout } = this.props;
    _.forEach(updateExpiredTimeouts, t => clearTimeout(t));
    const liveResults = _.get(response, 'data.types.epglisting.member', []);
    this.setState({ updateExpiredTimeouts: [] });
    if (!liveResults || !liveResults.length) return;

    const newTimeouts = [];
    _.forEach(liveResults, r => {
      const videoId = _.get(r, 'id');
      const endDate = _.get(r, 'endDate');
      if (!videoId || !endDate) return;

      const updateInMs = differenceInMilliseconds(stringToDate(endDate), now());
      if (updateInMs < 0) {
        this.updateExpiredProgram({ videoId });
      } else {
        const newTimeout = setTimeout(() => {
          this.updateExpiredProgram({ videoId });
        }, updateInMs);
        newTimeouts.push(newTimeout);
      }
    });
    this.setState({ updateExpiredTimeouts: newTimeouts });
  }

  updateExpiredProgram({ videoId }) {
    const { response } = this.state;
    const newResponse = _.cloneDeep(response);
    const liveResults = _.get(newResponse, 'data.types.epglisting.member', []);
    if (!liveResults || !liveResults.length || !videoId) return;
    _.remove(liveResults, r => _.get(r, 'id') === videoId);
    this.setState({ response: newResponse });
  }

  render() {
    const {
      renderSearchResults,
      onXClick,
      placeholderText,
      renderTabs,
      hideIcon,
      renderAltResults,
      isValidInput,
      localScrollContainer,
    } = this.props;

    const { resultsTimedOut } = this.state;
    const { response: searchResults, userInput, loading } = this.state;
    const tabs = isValidInput ? null : renderTabs({ searchResults });

    const icon = hideIcon ? null : (
      <div className={style.searchIcon}>
        <Icon icon="search" />
      </div>
    );
    const searchResultsTags = renderSearchResults({
      userInput,
      searchResults,
      loading,
      resultsTimedOut,
      clearTimer: this.clearTimer,
    });

    let altResultsClasses = style.altResultsContainer;

    if (isValidInput)
      altResultsClasses = classNames(altResultsClasses, style.altSearchedResultsContainer);

    const containerId = !localScrollContainer ? 'searchResultsScrollContainer' : null;

    return (
      <section className={style.search}>
        <SearchHeader onXClick={onXClick} />
        <div className={style.providerList}>
          <div className={style.searchInputContainer}>
            {icon}
            <input
              tabIndex="0"
              type="text"
              data-test="search-input"
              maxLength="50"
              className={style.searchInput}
              onChange={this.handleSearchInputChange.bind(this)}
              value={userInput}
              ref={input => {
                this.searchInput = input;
              }}
              placeholder={placeholderText}
              autoCorrect="off"
              autoComplete="off"
            />

            {tabs}
          </div>
          {renderAltResults ? null : searchResultsTags}
        </div>
        <div className={altResultsClasses} id={containerId}>
          {renderAltResults ? searchResultsTags : null}
        </div>
      </section>
    );
  }
}

Search.defaultProps = {
  autofocus: false,
  clearTimeout: noop,
  handleInputChange: noop,
  handleSearchCompleted: noop,
  hideIcon: false,
  onXClick: noop,
  placeholderText: '',
  renderAltResults: false,
  renderSearchResults: noop,
  renderTabs: noop,
  setTimeout: noop,
  toggleSearch: noop,
  useDoubleQuotes: false,
  getSearchResultCount: noop,
};

Search.propTypes = {
  autofocus: PropTypes.bool,
  clearTimeout: PropTypes.func,
  handleInputChange: PropTypes.func,
  handleSearchCompleted: PropTypes.func,
  hideIcon: PropTypes.bool,
  isValidInput: PropTypes.bool.isRequired,
  localScrollContainer: PropTypes.bool.isRequired,
  onXClick: PropTypes.func,
  placeholderText: PropTypes.string,
  renderAltResults: PropTypes.bool,
  renderSearchResults: PropTypes.func,
  renderTabs: PropTypes.func,
  request: PropTypes.shape({}).isRequired,
  setTimeout: PropTypes.func,
  toggleSearch: PropTypes.func,
  useDoubleQuotes: PropTypes.bool,
  getSearchResultCount: PropTypes.func,
};

export default ReactTimeout(connect(null, null)(Search));
